import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";
import {
  BellSimple,
  EnvelopeSimple,
  FileText,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import useAuth from "../../hooks/useAuth";
import { getHomeFlow } from "../../service/homeFlow";
import { fetchNotification } from "../../service/matter";
import {
  fetchInboxUnreadCount,
  selectInboxUnreadCount,
} from "../../slice/InboxSlice";
import { getFolderPath } from "../../utils/navigationUrl";
import { splitMatterLabel } from "../../utils/matterLabel";
import {
  CATEGORY_META,
  activationOf,
  ageTone,
  categoryOf,
  dueInfo,
  groupByClient,
  onAClockRows,
  partitionByActivation,
  stateLineFor,
} from "./MyWork/plate";
import WorkFold from "./MyWork/WorkFold";
import {
  AgeText,
  DueChip,
  KindDot,
  KindTag,
  MatterCardHeader,
  StateLine,
} from "./MyWork/MyWorkStyled";
import {
  ActivityBody,
  ActivityDot,
  ActivityMessage,
  ActivityRow,
  ActivityTime,
  GreetingBlock,
  GreetingDate,
  GreetingTitle,
  MobileHomeWrapper,
  ReviewRow,
  ReviewRowMeta,
  ReviewRowTitle,
  SearchPill,
  Section,
  SectionCard,
  SectionEmpty,
  SectionHeaderRow,
  SectionLink,
  SectionSubtitle,
  SectionTitle,
  StatCard,
  StatGrid,
  StatIconChip,
  StatLabel,
  StatValue,
  StatValueRow,
} from "./MobileHomeStyled";

/**
 * Mobile Home — a "Today" dashboard (Mobile DMS v1.1).
 *
 * Work sections mirror the 8/31 approved desktop redesign:
 *
 *   1. Personal greeting + date, search pill, unread cards (Inbox and
 *      notifications — unread counts change behavior, totals don't).
 *   2. **On a clock** — every deadline in the next two weeks or
 *      overdue, soonest first, so court dates never hide.
 *   3. **My work, by client** — one card per matter with all the work
 *      for that client; rows carry kind tag, from, due, and age.
 *   4. Recent activity feed.
 *
 * Phones are a read-and-glance surface: rows open their document or
 * matter; acting on work items stays on desktop. Local component
 * state on purpose — reusing HomeSlice would drag in the desktop
 * table pagination machinery for nothing.
 */

const PREVIEW_SIZE = 5;

const getGreeting = (date = new Date()) => {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const formatToday = (date = new Date()) =>
  date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

const formatRelative = (isoString) => {
  if (!isoString) return "";
  const ts = Date.parse(isoString);
  if (Number.isNaN(ts)) return "";
  const delta = Date.now() - ts;
  if (delta < 60 * 1000) return "just now";
  const minutes = Math.floor(delta / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const RowKindTag = ({ row }) => {
  const meta = CATEGORY_META[categoryOf(row)];
  return (
    <KindTag>
      <KindDot $color={meta.dot} />
      {meta.short}
    </KindTag>
  );
};

RowKindTag.propTypes = { row: PropTypes.object.isRequired };

const WorkRowButton = ({ row, onOpen, showMatter }) => {
  const due = dueInfo(row?.due_on);
  const tone = ageTone(row?.age_days || 0);
  const matter = splitMatterLabel(row?.matter_name);
  // Blocked rows read greyed with the "Coming up…" line in place of
  // the usual due/age meta — the mobile twin of the desktop card
  // (Matthew, 9/4). Phones are read-and-glance: no verbs here either
  // way, so there's nothing further to suppress.
  const blocked = activationOf(row) === "blocked";
  const stateLine = stateLineFor(row);
  return (
    <ReviewRow type="button" onClick={() => onOpen(row)} style={blocked ? { opacity: 0.55 } : undefined}>
      <ReviewRowTitle>{row?.title || "Untitled"}</ReviewRowTitle>
      <ReviewRowMeta>
        <RowKindTag row={row} />
        {showMatter && matter.short && (
          <span title={matter.full}>{matter.short}</span>
        )}
        {row?.counterparty && <span>from {row.counterparty}</span>}
        {stateLine ? (
          <StateLine>{stateLine}</StateLine>
        ) : (
          <>
            {due && (
              <DueChip $urgency={due.urgency} data-urgency={due.urgency}>
                due {due.label} · {due.detail}
              </DueChip>
            )}
            {(row?.age_days || 0) > 0 && (
              <AgeText $tone={tone}>{row.age_days}d old</AgeText>
            )}
          </>
        )}
      </ReviewRowMeta>
    </ReviewRow>
  );
};

WorkRowButton.propTypes = {
  row: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
  showMatter: PropTypes.bool,
};

const MobileHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const inboxUnreadCount = useSelector(selectInboxUnreadCount);

  const [work, setWork] = useState({
    status: "loading",
    mine: [],
    salesforceDegraded: false,
  });
  const [activity, setActivity] = useState({
    status: "loading",
    items: [],
    unreadCount: 0,
  });

  useEffect(() => {
    dispatch(fetchInboxUnreadCount());

    let cancelled = false;
    (async () => {
      try {
        const response = await getHomeFlow();
        if (cancelled) return;
        const data = response?.data || {};
        setWork({
          status: "ready",
          mine: Array.isArray(data.in_my_court) ? data.in_my_court : [],
          // Same honesty as desktop: Salesforce not answering means
          // work items are missing from this refresh, not "all clear".
          salesforceDegraded:
            data.work_items_enabled === true && data.salesforce_ok === false,
        });
      } catch (err) {
        console.error("Failed to load work flow:", err);
        if (!cancelled) {
          setWork({ status: "failed", mine: [], salesforceDegraded: false });
        }
      }
    })();

    (async () => {
      try {
        const response = await fetchNotification({ offset: 0, limit: PREVIEW_SIZE });
        if (cancelled) return;
        const data = response?.data || {};
        setActivity({
          status: "ready",
          items: Array.isArray(data.results) ? data.results : [],
          unreadCount:
            typeof data.unread_count === "number" ? data.unread_count : 0,
        });
      } catch (err) {
        console.error("Failed to load activity feed:", err);
        if (!cancelled) {
          setActivity({ status: "failed", items: [], unreadCount: 0 });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const firstName =
    (user?.first_name || "").trim() ||
    (user?.username || "").trim() ||
    (user?.email || "").split("@")[0] ||
    "";

  const handleOpenRow = useCallback(
    (row) => {
      const doc = row?.document || {};
      if (doc.relative_path) {
        const { matterId, documentId } = getFolderPath(doc.relative_path);
        if (matterId && documentId) {
          navigate(
            `/matter/${matterId}?doc=${encodeURIComponent(documentId)}`,
            {
              state: {
                documentName: row?.title || "",
                documentFileType: doc.file_type || "",
              },
            }
          );
          return;
        }
      }
      if (row?.matter_id) navigate(`/matter/${row.matter_id}`);
    },
    [navigate]
  );

  const clocked = onAClockRows(work.mine);
  const clientGroups = groupByClient(work.mine);

  // Unread counts change behavior; work totals don't (Matthew, 8/31).
  const stats = [
    {
      key: "inbox",
      label: "Unread in Inbox",
      value: inboxUnreadCount,
      Icon: EnvelopeSimple,
      bg: "#E6F0FC",
      fg: "#0C53BB",
      onClick: () => navigate("/inbox"),
    },
    {
      key: "notifications",
      label: "New notifications",
      value: activity.status === "loading" ? null : activity.unreadCount,
      Icon: BellSimple,
      bg: "#F1F6FE",
      fg: "#0C53BB",
      onClick: () => navigate("/notifications"),
    },
  ];

  return (
    <MobileHomeWrapper>
      <GreetingBlock>
        <GreetingDate>{formatToday()}</GreetingDate>
        <GreetingTitle>
          {getGreeting()}
          {firstName ? `, ${firstName}` : ""}
        </GreetingTitle>
      </GreetingBlock>

      <SearchPill
        type="button"
        onClick={() => navigate("/search")}
        aria-label="Search matters and documents"
      >
        <MagnifyingGlass size={18} />
        Search matters &amp; documents
      </SearchPill>

      <StatGrid>
        {stats.map(({ key, label, value, Icon, bg, fg, onClick }) => (
          <StatCard key={key} type="button" onClick={onClick} aria-label={label}>
            <StatIconChip $bg={bg} $fg={fg}>
              <Icon size={18} weight="fill" />
            </StatIconChip>
            <StatValueRow>
              <StatValue>
                {value == null ? "–" : value > 99 ? "99+" : value}
              </StatValue>
              <StatLabel>{label}</StatLabel>
            </StatValueRow>
          </StatCard>
        ))}
      </StatGrid>

      {work.status === "ready" && clocked.length > 0 && (
        <Section aria-label="Due Soon">
          <SectionHeaderRow>
            <SectionTitle>Due Soon</SectionTitle>
          </SectionHeaderRow>
          <SectionCard>
            {clocked.map((row) => (
              <WorkRowButton
                key={`clock-${row.key}`}
                row={row}
                onOpen={handleOpenRow}
                showMatter
              />
            ))}
          </SectionCard>
        </Section>
      )}

      <Section aria-label="My work">
        <SectionHeaderRow>
          <SectionTitle>My work</SectionTitle>
        </SectionHeaderRow>
        <SectionSubtitle>by client — all the work for a case in one card</SectionSubtitle>
        {work.status === "loading" && (
          <SectionCard>
            <SectionEmpty role="status">
              <Spin size="small" />
              Gathering your work…
            </SectionEmpty>
          </SectionCard>
        )}
        {work.status === "failed" && (
          <SectionCard>
            <SectionEmpty role="alert">
              Couldn&apos;t load your work — check your connection and try
              again.
            </SectionEmpty>
          </SectionCard>
        )}
        {work.status === "ready" && work.salesforceDegraded && (
          <SectionCard>
            <SectionEmpty role="alert">
              Salesforce didn&apos;t answer, so work items are missing from
              this refresh — reviews, signatures, and tasks are current.
            </SectionEmpty>
          </SectionCard>
        )}
        {work.status === "ready" &&
          work.mine.length === 0 &&
          !work.salesforceDegraded && (
            <SectionCard>
              <SectionEmpty role="status">
                <FileText size={28} weight="duotone" color="#98A6BA" />
                You&apos;re all caught up. Nice.
              </SectionEmpty>
            </SectionCard>
          )}
        {work.status === "ready" &&
          clientGroups.map((group) => {
            const matter = splitMatterLabel(group.matterName);
            const { active, outside, snoozed } = partitionByActivation(
              group.rows
            );
            return (
              <SectionCard key={group.key}>
                <MatterCardHeader title={matter.full || undefined}>
                  {matter.short || "No matter"}
                </MatterCardHeader>
                {active.map((row) => (
                  <WorkRowButton
                    key={row.key}
                    row={row}
                    onOpen={handleOpenRow}
                    showMatter={false}
                  />
                ))}
                <WorkFold
                  kind="outside"
                  title="Waiting on client / vendor"
                  rows={outside}
                >
                  {outside.map((row) => (
                    <WorkRowButton
                      key={row.key}
                      row={row}
                      onOpen={handleOpenRow}
                      showMatter={false}
                    />
                  ))}
                </WorkFold>
                <WorkFold kind="snoozed" title="Snoozed" rows={snoozed}>
                  {snoozed.map((row) => (
                    <WorkRowButton
                      key={row.key}
                      row={row}
                      onOpen={handleOpenRow}
                      showMatter={false}
                    />
                  ))}
                </WorkFold>
              </SectionCard>
            );
          })}
      </Section>

      <Section aria-label="Recent activity">
        <SectionHeaderRow>
          <SectionTitle>Recent activity</SectionTitle>
          <SectionLink type="button" onClick={() => navigate("/notifications")}>
            View all
          </SectionLink>
        </SectionHeaderRow>
        <SectionCard>
          {activity.status === "loading" && (
            <SectionEmpty role="status">
              <Spin size="small" />
              Loading activity…
            </SectionEmpty>
          )}
          {activity.status === "failed" && (
            <SectionEmpty role="alert">
              Couldn&apos;t load recent activity.
            </SectionEmpty>
          )}
          {activity.status === "ready" && activity.items.length === 0 && (
            <SectionEmpty role="status">
              <BellSimple size={28} weight="duotone" color="#98A6BA" />
              No recent activity.
            </SectionEmpty>
          )}
          {activity.status === "ready" &&
            activity.items.map((item) => (
              <ActivityRow key={item.id}>
                <ActivityDot $unread={!item.is_read} aria-hidden="true" />
                <ActivityBody>
                  <ActivityMessage>
                    {item.message || item.title || "Notification"}
                  </ActivityMessage>
                  <ActivityTime>{formatRelative(item.created_at)}</ActivityTime>
                </ActivityBody>
              </ActivityRow>
            ))}
        </SectionCard>
      </Section>
    </MobileHomeWrapper>
  );
};

export default MobileHome;
